import { useState, useEffect } from "react";
import { authService } from "./services/authService";
import { supabase, estadoService } from "./services/supabase";
import { LoginScreenEmail, LogoutButton } from "./components/AuthEmail";
import CampoIA from "./App";
import { C } from "./components/ui";

export default function AppWithAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campoActivo, setCampoActivo] = useState(null);
  const [camposDisponibles, setCamposDisponibles] = useState([]);

  useEffect(() => {
    const usuarioGuardado = authService.obtenerUsuario();
    if (usuarioGuardado) setUser(usuarioGuardado);
    setLoading(false);
  }, []);

  // Los campos se cargan cuando hay usuario, no solo al montar: si se hacía solo
  // al montar, después de iniciar sesión `camposDisponibles` quedaba vacío y el
  // selector de campo no aparecía hasta recargar la página a mano.
  useEffect(() => {
    if (user?.id) cargarCamposDisponibles(user);
  }, [user?.id]);

  const cargarCamposDisponibles = async (usuario) => {
    try {
      // Campos propios (todas las filas de campos_metadata del usuario)
      const metaCampos = await estadoService.listarCampos(usuario.id);
      let campos = metaCampos.map(c => ({
        id: c.campo_id, nombre: c.nombre_campo || "Mi campo", propietario: usuario.id, esPropio: true
      }));
      // Asegurar el campo por defecto (id = user.id) aunque no tenga metadata todavía
      if (!campos.some(c => c.id === usuario.id)) {
        campos = [{ id: usuario.id, nombre: usuario.nombre_campo || "Mi campo", propietario: usuario.id, esPropio: true }, ...campos];
      }

      // Agregar campos compartidos
      const accesos = await authService.obtenerAccesoCampos(usuario.id);

      // Un mismo campo puede venir repetido: si se otorgó el acceso más de una vez
      // hay varias filas en permisos_campos con el mismo propietario. Además puede
      // haber un permiso hacia uno mismo, que duplicaría el campo propio. Las dos
      // cosas metían opciones con el mismo `value` en el <select>, y elegirlas
      // quedaba ambiguo.
      const idsPropios = new Set(campos.map(c => c.id));
      const propietariosUnicos = [...new Set(
        accesos
          .filter(a => a.usuario_acceso_id === usuario.id) // Campos de otros que me compartieron
          .map(a => a.usuario_propietario_id)
          .filter(id => id && id !== usuario.id && !idsPropios.has(id))
      )];

      // Obtener info de propietarios para mostrar nombres reales
      const compartidos = await Promise.all(
        propietariosUnicos
          .map(async (propietarioId) => {
            try {
              const propietarioInfo = await supabase
                .from("usuarios")
                .select("nombre_campo")
                .eq("id", propietarioId)
                .single();

              const nombrePropietario = propietarioInfo.data?.nombre_campo || propietarioId;
              return {
                id: propietarioId,
                nombre: `Campo de ${nombrePropietario}`,
                propietario: propietarioId,
                esPropio: false
              };
            } catch {
              return {
                id: propietarioId,
                nombre: `Campo compartido`,
                propietario: propietarioId,
                esPropio: false
              };
            }
          })
      );

      setCamposDisponibles([...campos, ...compartidos]);
      setCampoActivo(prev => prev || campos[0]); // preserva la selección actual
    } catch (e) {
      console.error("Error cargando campos:", e);
      setCamposDisponibles([{ id: usuario.id, nombre: usuario.nombre_campo || "Mi campo", propietario: usuario.id, esPropio: true }]);
      setCampoActivo({ id: usuario.id, nombre: usuario.nombre_campo || "Mi campo", propietario: usuario.id, esPropio: true });
    }
  };

  if (loading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: C.fondo,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🌾</div>
          <div>Cargando...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreenEmail onLogin={(u) => setUser(u)} />;
  }

  const handleCampoCreado = () => {
    // Refrescar lista de campos disponibles
    cargarCamposDisponibles(user);
  };

  return (
    <CampoIA
      user={user}
      profile={{
        ...user,
        campo_propietario_id: campoActivo?.id,
        campo_es_propio: campoActivo?.esPropio,
        campo_owner_id: campoActivo?.propietario,
        features_acceso: campoActivo?.features // Features del acceso compartido (si aplica)
      }}
      onProfileUpdate={(p) => setUser((prev) => ({ ...prev, ...p }))}
      LogoutButton={() => <LogoutButton user={user} onLogout={() => {
        // Limpiar el campo activo: `cargarCamposDisponibles` conserva la selección
        // previa (`prev || campos[0]`), así que si no se resetea, al entrar con
        // otro usuario queda seleccionado el campo del usuario anterior.
        setCampoActivo(null); setCamposDisponibles([]);
        setUser(null); authService.logout();
      }} />}
      camposDisponibles={camposDisponibles}
      campoActivo={campoActivo}
      onCampoChange={(campo) => setCampoActivo(campo)}
      onCampoCreado={handleCampoCreado}
    />
  );
}
